import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";

import { fetchAllBuilds } from "@admin/api/build";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { Box, TableRow, Typography, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { ActivityIcon } from "@shared/Loading";
import { DateCell } from "@shared/table/DateCell";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { showErrorToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import TableItem from "./TableItem";

type Status = "pending" | "fulfilled" | "rejected" | "uninitialized";
export type State = {
    data: API.AdminBuild[];
    count: number;
    status: Status;
};
const defaultState: State = {
    data: [],
    count: 0,
    status: "uninitialized",
};

const StatusIcon = ({ buildStatus }: { buildStatus: API.AdminBuild["buildStatus"] | null }) => {
    switch (buildStatus) {
        case "running":
            return <ActivityIcon tooltip={i18next.t("pages:productPage.buildsTable.status.running")} />;
        case "pending":
            return (
                <Tooltip title={i18next.t("pages:productPage.buildsTable.status.pending")}>
                    <i className="fa-solid fa-hourglass-half" />
                </Tooltip>
            );
        default:
            return (
                <Tooltip title={i18next.t("pages:productPage.buildsTable.status.unknown")}>
                    <i className="fa-solid fa-circle-question" />
                </Tooltip>
            );
    }
};

const columnHelper = createColumnHelper<API.AdminBuild>();

const tableColumns = [
    columnHelper.accessor("buildStatus", {
        id: "buildStatus",
        header: i18next.t("common:label.status"),
        cell: (info) => (
            <MinimalTableCell>
                <StatusIcon buildStatus={info.getValue()} />
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("buildId", {
        id: "buildId",
        header: i18next.t("common:label.id"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("codename", {
        id: "codename",
        header: i18next.t("common:label.codename"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("version", {
        id: "version",
        header: i18next.t("common:label.version"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("created", {
        id: "created",
        header: i18next.t("common:label.created"),
        cell: (info) => <DateCell value={info.getValue()} />,
        enableSorting: true,
    }),
    columnHelper.accessor("metadata.user.tags", {
        id: "tags",
        header: i18next.t("common:label.tags"),
        cell: (info) => {
            const theme = useTheme();
            const tags = info.getValue() || [];
            const hasTags = tags.length > 0;

            return (
                <MinimalTableCell>
                    {hasTags ? (
                        <div style={{ display: "flex", gap: theme.spacing(1), flexWrap: "wrap" }}>
                            {tags.map((tag: { key: string; value: string }) => (
                                <Tag key={tag.key} item={tag} />
                            ))}
                        </div>
                    ) : (
                        <Typography>-</Typography>
                    )}
                </MinimalTableCell>
            );
        },
        enableSorting: true,
    }),
];

const AdminBuildsPage = () => {
    const [state, setState] = React.useState<State>(defaultState);

    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["common", "pages"]);

    const table = useReactTable<API.AdminBuild>({
        data: state.data,
        columns: tableColumns,
        initialState: {
            sorting: [{ id: "version", desc: true }],
            pagination: {
                pageIndex: 0,
                pageSize: 12,
            },
        },
        // turn off client-side pagination
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    const { pageIndex, pageSize } = table.getState().pagination;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, pageSize, state.data.length]);

    React.useLayoutEffect(() => {
        setState((prev) => ({ ...prev, status: "pending" }));
        void fetchAllBuilds()
            .then((data) => setState({ data: data.list, count: data.count, status: "fulfilled" }))
            .catch((error) => {
                const err = error as Error;
                showErrorToast(err.message);
                setState((prev) => ({ ...prev, status: "rejected" }));
            });
    }, [pageIndex, pageSize]);

    if (state.status === "uninitialized") {
        return <PageContainer isLoading />;
    }

    return (
        <PageContainer fitViewport>
            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                        <MinimalTableHeaderCell />
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody
                                table={table}
                                loading={state.status === "pending"}
                                noDataMessage={t("pages:adminBuildsPage.noBuildsInTheQueue")}
                            >
                                {table.getRowModel().rows.map((row) => (
                                    <TableItem key={row.id} setState={setState} row={row} />
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>
                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={state.data.length}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => table.setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        table.setPagination({ pageIndex: 0, pageSize: size });
                    }}
                />
            </Box>
        </PageContainer>
    );
};
export default withRequiredRole(
    withMetaDescription(AdminBuildsPage, {
        title: i18next.t("pages:adminBuildsPage.metaTitle"),
        description: i18next.t("pages:adminBuildsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
